/**
 * Validates URL
 * Ensures URL is relative and doesn't contain protocol schemes or dangerous patterns
 * @param {string} url - The URL to validate
 * @returns {string} - Validated URL
 * @throws {Error} - If URL is invalid or unsafe
 */
export const validateUrl = url => {
    if (!url || typeof url !== 'string') {
        throw new Error('URL must be a non-empty string');
    }

    // Ensure URL is relative (starts with /) to prevent protocol injection
    if (!url.startsWith('/')) {
        throw new Error('URL must be a relative path starting with /');
    }

    // Prevent protocol schemes (http://, https://, file://, etc.)
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) {
        throw new Error('URL must not contain protocol schemes');
    }

    // Prevent path traversal sequences
    if (/\.\.(\/|\\|%2F|%5C)/i.test(url)) {
        throw new Error('URL must not contain path traversal sequences');
    }

    // Prevent javascript: and data: schemes
    if (/^(javascript|data):/i.test(url)) {
        throw new Error('URL must not contain javascript: or data: schemes');
    }

    return url;
};

/**
 * Validates extId parameter to prevent SSRF attacks
 * Only allows alphanumeric characters, hyphens, and underscores
 * @param {string} extId - Login request identifier to validate
 * @returns {string} - Validated extId
 * @throws {Error} - If extId is invalid or unsafe
 */
export const validateExtId = extId => {
    if (!extId || typeof extId !== 'string') {
        throw new Error('extId must be a non-empty string');
    }

    // Whitelist approach: only allow alphanumeric, hyphens, and underscores
    // This prevents injection of path traversal, protocol schemes, or other dangerous characters
    const safeExtIdPattern = /^[a-zA-Z0-9_-]+$/;
    if (!safeExtIdPattern.test(extId)) {
        throw new Error(
            'extId contains invalid characters. Only alphanumeric characters, hyphens, and underscores are allowed'
        );
    }

    // Additional length check to prevent extremely long values
    if (extId.length > 255) {
        throw new Error('extId exceeds maximum length of 255 characters');
    }

    return extId;
};

/**
 * Get a cookie item
 * @param {string} name - The name of the cookie to get
 * @returns {string} - The value of the cookie
 */
export const getCookieItem = name => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [cookieName, ...rest] = cookie.trim().split('=');
        if (cookieName === name) {
            return decodeURIComponent(rest.join('=')) || null;
        }
    }
    return null;
};
